/**
 * Registry of experimental movement rules. All are registered but disabled
 * by default; enabling them is an explicit, recorded configuration change
 * (it changes the record key, so experimental times never mix with baseline).
 */
import { MovementRuleSet } from '../MovementRules';
import { createBendingRule } from './GeometricBending';
import { createCompressionRules } from './ImpactCompression';
import { createCouplingRule } from './SurfaceCoupling';

export interface ExperimentInfo {
  key: 'A' | 'B' | 'C';
  name: string;
  /** Rule ids in enable order. */
  rules: readonly string[];
}

export type ExperimentKey = ExperimentInfo['key'];

export const EXPERIMENTS: readonly ExperimentInfo[] = [
  { key: 'A', name: 'Geometric bending', rules: ['exp.bending'] },
  { key: 'B', name: 'Impact compression', rules: ['exp.compression.store', 'exp.compression.release'] },
  { key: 'C', name: 'Surface coupling', rules: ['exp.coupling'] },
];

export function createRuleSet(): MovementRuleSet {
  const set = new MovementRuleSet();
  set.register(createBendingRule());
  const [store, release] = createCompressionRules();
  set.register(store);
  set.register(release);
  set.register(createCouplingRule());
  return set;
}

export function setExperimentEnabled(set: MovementRuleSet, key: ExperimentInfo['key'], on: boolean): void {
  const exp = EXPERIMENTS.find((e) => e.key === key);
  if (!exp) throw new Error(`Unknown experiment ${key}`);
  const order = on ? exp.rules : [...exp.rules].reverse();
  for (const id of order) set.setEnabled(id, on);
}

export function isExperimentEnabled(set: MovementRuleSet, key: ExperimentInfo['key']): boolean {
  const exp = EXPERIMENTS.find((e) => e.key === key);
  return !!exp && exp.rules.every((id) => set.isEnabled(id));
}
